<template>
    <div class="audit-viewer">
        <!-- Summary Bar -->
        <div class="audit-summary-bar">
            <div class="summary-pill">
                <span class="pill-label">Table</span>
                <span class="pill-value">{{ summary.tableName }}</span>
            </div>
            <div class="summary-pill">
                <span class="pill-label">Rows</span>
                <span class="pill-value">{{ summary.totalRows }}</span>
            </div>
            <div class="summary-pill">
                <span class="pill-label">Columns</span>
                <span class="pill-value">{{ summary.totalColumns }}</span>
            </div>
            <div v-if="summary.sourceFormat" class="summary-pill source-format-pill">
                <i class="fa-solid fa-brackets-curly" style="font-size: 0.65rem;"></i>
                <span class="pill-value">{{ summary.sourceFormat }}</span>
            </div>
            <div class="ms-auto d-flex align-items-center gap-2">
                <button class="audit-ctrl-btn" @click="expandAll" title="Expand all">
                    <i class="fa-solid fa-chevron-down"></i>
                    <span>All</span>
                </button>
                <button class="audit-ctrl-btn" @click="collapseAll" title="Collapse all">
                    <i class="fa-solid fa-chevron-right"></i>
                    <span>None</span>
                </button>
            </div>
        </div>

        <!-- Identity Namespaces (optional) -->
        <div v-if="identityNamespaces.length > 0" class="audit-section-block mb-2">
            <div class="audit-section-header" @click="toggleNsPanel">
                <i class="fa-solid fa-id-badge section-icon" style="color: #6366f1;"></i>
                <span>Identity Namespaces</span>
                <span class="section-count">{{ identityNamespaces.length }}</span>
                <i :class="['fa-solid ms-auto', nsPanelOpen ? 'fa-chevron-up' : 'fa-chevron-down']" style="font-size: 0.7rem; color: #94a3b8;"></i>
            </div>
            <div v-show="nsPanelOpen" class="ns-table-wrap">
                <table class="audit-table">
                    <thead>
                        <tr>
                            <th>Namespace</th>
                            <th>Columns</th>
                            <th>Fill Rate</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="ns in identityNamespaces" :key="ns.name">
                            <td><code class="ns-code">{{ ns.name }}</code></td>
                            <td>{{ ns.columns }}</td>
                            <td>
                                <span :class="['fill-badge', getFillClass(ns.fillRate)]">
                                    {{ ns.fillRateStr }}
                                    <span v-if="ns.sparse" class="ms-1">⚠️</span>
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Column Analysis -->
        <div class="audit-columns-header">
            <i class="fa-solid fa-table-columns" style="color: #3b82f6; font-size: 0.75rem;"></i>
            <span>Column Analysis</span>
            <span class="section-count">{{ columns.length }}</span>
        </div>

        <div class="audit-columns-list">
            <div
                v-for="(col, idx) in columns"
                :key="col.name"
                :class="['audit-col-item', openCols.has(idx) ? 'is-open' : '']"
            >
                <!-- Column Header Row (always visible) -->
                <div class="col-header" @click="toggleCol(idx)">
                    <div class="col-header-left">
                        <i :class="['fa-solid col-toggle-icon', openCols.has(idx) ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
                        <span class="col-path">{{ col.displayPath }}</span>
                        <code v-if="col.flatName" class="col-flat-name">{{ col.flatName }}</code>
                    </div>
                    <div class="col-header-right">
                        <!-- Fill rate mini-bar -->
                        <div class="fill-bar-wrap" :title="`Fill Rate: ${col.fillRate}%`">
                            <div class="fill-bar-track">
                                <div class="fill-bar-fill" :style="{ width: col.fillRate + '%', backgroundColor: getFillColor(col.fillRate) }"></div>
                            </div>
                            <span class="fill-pct" :style="{ color: getFillColor(col.fillRate) }">{{ col.fillRate }}% fill</span>
                        </div>
                        <!-- Cardinality badge -->
                        <span v-if="col.cardinalityLabel" :class="['card-badge', getCardinalityClass(col.cardinalityLabel)]" :title="`Cardinality Score: ${col.cardinalityScore}`">
                            {{ col.cardinalityLabel }}
                        </span>
                        <!-- Quick stats chips -->
                        <span class="stat-chip" title="Distinct Values">
                            <i class="fa-solid fa-fingerprint"></i> {{ col.distinctValues }}
                        </span>
                        <span class="stat-chip" title="Max Length">
                            <i class="fa-solid fa-ruler-horizontal"></i> {{ col.maxLength }}
                        </span>
                    </div>
                </div>

                <!-- Expanded Content -->
                <div v-show="openCols.has(idx)" class="col-body">
                    <!-- Value Distribution / First/Last tables in a horizontal flex layout -->
                    <div class="col-tables-row">
                        <div v-if="col.valueDistribution.length > 0" class="col-table-block">
                            <div class="col-table-label">Value Distribution</div>
                            <table class="audit-table">
                                <thead><tr><th>Value</th><th>Count</th></tr></thead>
                                <tbody>
                                    <tr v-for="(row, ri) in col.valueDistribution" :key="ri">
                                        <td>{{ row.value }}</td>
                                        <td>{{ row.count }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div v-if="col.first10.length > 0" class="col-table-block">
                            <div class="col-table-label">First 10 Values</div>
                            <table class="audit-table">
                                <thead><tr><th>Value</th><th>Count</th></tr></thead>
                                <tbody>
                                    <tr v-for="(row, ri) in col.first10" :key="ri">
                                        <td>{{ row.value }}</td>
                                        <td>{{ row.count }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div v-if="col.last10.length > 0" class="col-table-block">
                            <div class="col-table-label">Last 10 Values</div>
                            <table class="audit-table">
                                <thead><tr><th>Value</th><th>Count</th></tr></thead>
                                <tbody>
                                    <tr v-for="(row, ri) in col.last10" :key="ri">
                                        <td>{{ row.value }}</td>
                                        <td>{{ row.count }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <!-- If no tables at all, show a quiet empty state -->
                        <div v-if="col.valueDistribution.length === 0 && col.first10.length === 0 && col.last10.length === 0" class="col-no-data">
                            No distribution data available.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "AuditReportViewer",
    props: {
        content: {
            type: String,
            default: ""
        }
    },
    data() {
        return {
            summary: { tableName: "—", totalRows: "—", totalColumns: "—", sourceFormat: null },
            identityNamespaces: [],
            columns: [],
            openCols: new Set(),
            nsPanelOpen: true,
        };
    },
    watch: {
        content: {
            immediate: true,
            handler(val) {
                if (val) this.parseMarkdown(val);
            }
        }
    },
    methods: {
        parseMarkdown(md) {
            const lines = md.split("\n");
            const summary = {};
            const identityNamespaces = [];
            const columns = [];

            let section = null;       // 'summary' | 'identity' | 'columns'
            let currentCol = null;
            let currentTable = null;  // 'dist' | 'first10' | 'last10'
            let inTableBody = false;

            const flushCol = () => {
                if (currentCol) columns.push(currentCol);
                currentCol = null;
                currentTable = null;
                inTableBody = false;
            };

            for (let i = 0; i < lines.length; i++) {
                const line = lines[i];
                const trimmed = line.trim();

                // ── Section headings ──────────────────────────────────────
                if (trimmed.startsWith("## Summary")) { section = "summary"; continue; }
                if (trimmed.startsWith("## Identity Namespaces")) { section = "identity"; continue; }
                if (trimmed.startsWith("## Column Analysis")) { flushCol(); section = "columns"; continue; }

                // ── Summary section ───────────────────────────────────────
                if (section === "summary") {
                    const tableMatch = trimmed.match(/\*\*Table Name\*\*:\s*(.+)/);
                    const rowsMatch = trimmed.match(/\*\*Total Rows\*\*:\s*(.+)/);
                    const colsMatch = trimmed.match(/\*\*Total Columns\*\*:\s*(.+)/);
                    const fmtMatch = trimmed.match(/\*\*Source Format\*\*:\s*(.+)/);
                    if (tableMatch) summary.tableName = tableMatch[1];
                    if (rowsMatch) summary.totalRows = rowsMatch[1];
                    if (colsMatch) summary.totalColumns = colsMatch[1];
                    if (fmtMatch) summary.sourceFormat = fmtMatch[1];
                    continue;
                }

                // ── Identity Namespaces table ────────────────────────────
                if (section === "identity") {
                    if (trimmed.startsWith("|") && !trimmed.startsWith("|-")) {
                        const cells = trimmed.split("|").map(c => c.trim()).filter(Boolean);
                        if (cells.length >= 3 && cells[0] !== "Namespace") {
                            const rawFill = cells[2].replace("⚠️ sparse", "").trim();
                            const fillNum = parseFloat(rawFill);
                            identityNamespaces.push({
                                name: cells[0].replace(/`/g, ""),
                                columns: cells[1],
                                fillRateStr: cells[2].replace("⚠️ sparse", "").trim(),
                                fillRate: isNaN(fillNum) ? null : fillNum,
                                sparse: cells[2].includes("sparse")
                            });
                        }
                    }
                    continue;
                }

                // ── Column Analysis section ──────────────────────────────
                if (section === "columns") {
                    // ### column heading
                    if (trimmed.startsWith("### ")) {
                        flushCol();
                        const raw = trimmed.slice(4).trim();
                        // detect backtick-quoted path vs plain name
                        const pathMatch = raw.match(/^`(.+)`$/);
                        currentCol = {
                            displayPath: pathMatch ? pathMatch[1] : raw,
                            flatName: null,
                            fillRate: 0,
                            maxLength: "—",
                            distinctValues: "—",
                            cardinalityScore: null,
                            cardinalityLabel: null,
                            valueDistribution: [],
                            first10: [],
                            last10: [],
                        };
                        currentTable = null;
                        inTableBody = false;
                        continue;
                    }

                    if (!currentCol) continue;

                    // flat name
                    const flatMatch = trimmed.match(/\*Flattened column name:\s*`(.+)`\*/);
                    if (flatMatch) { currentCol.flatName = flatMatch[1]; continue; }

                    // stats
                    const fillMatch = trimmed.match(/\*\*Fill Rate\*\*:\s*([\d.]+)%/);
                    if (fillMatch) { currentCol.fillRate = parseFloat(fillMatch[1]); continue; }

                    const maxLenMatch = trimmed.match(/\*\*Max Length\*\*:\s*(.+)/);
                    if (maxLenMatch) { currentCol.maxLength = maxLenMatch[1]; continue; }

                    const distinctMatch = trimmed.match(/\*\*Distinct Values\*\*:\s*(.+)/);
                    if (distinctMatch) { currentCol.distinctValues = distinctMatch[1]; continue; }

                    const cardMatch = trimmed.match(/\*\*Cardinality Score\*\*:\s*([\d.]+)\s*\((.+)\)/);
                    if (cardMatch) {
                        currentCol.cardinalityScore = cardMatch[1];
                        currentCol.cardinalityLabel = cardMatch[2];
                        continue;
                    }

                    // sub-section table labels
                    if (trimmed.startsWith("#### Value Distribution")) { currentTable = "dist"; inTableBody = false; continue; }
                    if (trimmed.startsWith("#### First 10 Values")) { currentTable = "first10"; inTableBody = false; continue; }
                    if (trimmed.startsWith("#### Last 10 Values")) { currentTable = "last10"; inTableBody = false; continue; }

                    // table rows
                    if (currentTable && trimmed.startsWith("|")) {
                        if (trimmed.startsWith("|-")) { inTableBody = true; continue; }
                        if (!inTableBody) continue; // header row
                        const cells = trimmed.split("|").map(c => c.trim()).filter(Boolean);
                        if (cells.length >= 2) {
                            const entry = { value: cells[0].replace(/\\\|/g, "|"), count: cells[1] };
                            if (currentTable === "dist") currentCol.valueDistribution.push(entry);
                            else if (currentTable === "first10") currentCol.first10.push(entry);
                            else if (currentTable === "last10") currentCol.last10.push(entry);
                        }
                    }
                }
            }

            flushCol();
            this.summary = summary;
            this.identityNamespaces = identityNamespaces;
            this.columns = columns;
            // By default open first 3 columns so the page has immediate content
            this.openCols = new Set([0, 1, 2].filter(i => i < columns.length));
        },

        toggleCol(idx) {
            const s = new Set(this.openCols);
            s.has(idx) ? s.delete(idx) : s.add(idx);
            this.openCols = s;
        },
        toggleNsPanel() { this.nsPanelOpen = !this.nsPanelOpen; },
        expandAll() { this.openCols = new Set(this.columns.map((_, i) => i)); },
        collapseAll() { this.openCols = new Set(); },

        getFillColor(pct) {
            if (pct >= 90) return "#22c55e";
            if (pct >= 50) return "#f59e0b";
            return "#ef4444";
        },
        getFillClass(pct) {
            if (pct === null) return "fill-unknown";
            if (pct >= 90) return "fill-high";
            if (pct >= 50) return "fill-mid";
            return "fill-low";
        },
        getCardinalityClass(label) {
            const l = (label || "").toLowerCase();
            if (l.includes("unique") || l.includes("high")) return "card-unique";
            if (l.includes("medium")) return "card-medium";
            return "card-low";
        }
    }
}
</script>

<style scoped>
/* ── Root ──────────────────────────────────────────────────────── */
.audit-viewer {
    font-size: 0.78rem;
    font-family: inherit;
    color: var(--bs-body-color, #212529);
}

/* ── Summary Bar ───────────────────────────────────────────────── */
.audit-summary-bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.4rem;
    padding: 0.5rem 0.75rem;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    margin-bottom: 0.6rem;
}

.summary-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    padding: 0.18rem 0.55rem;
    font-size: 0.72rem;
    line-height: 1.4;
}
.pill-label {
    color: #94a3b8;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.63rem;
    letter-spacing: 0.04em;
}
.pill-value {
    color: #1e293b;
    font-weight: 600;
}
.source-format-pill { color: #6366f1; border-color: #c7d2fe; background: #eef2ff; }
.source-format-pill .pill-value { color: #4f46e5; }

/* ── Ctrl Buttons ──────────────────────────────────────────────── */
.audit-ctrl-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    border: 1px solid #e2e8f0;
    background: #fff;
    border-radius: 5px;
    font-size: 0.68rem;
    font-weight: 600;
    color: #64748b;
    padding: 0.18rem 0.5rem;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
}
.audit-ctrl-btn:hover { background: #f1f5f9; border-color: #cbd5e1; color: #334155; }

/* ── Section Block (Identity Namespaces) ───────────────────────── */
.audit-section-block {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
}
.audit-section-header {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.45rem 0.75rem;
    background: #f8fafc;
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: 600;
    color: #334155;
    user-select: none;
    border-bottom: 1px solid transparent;
    transition: background 0.12s;
}
.audit-section-header:hover { background: #f1f5f9; }
.section-icon { font-size: 0.7rem; }
.section-count {
    font-size: 0.65rem;
    background: #e2e8f0;
    color: #64748b;
    border-radius: 999px;
    padding: 0.1rem 0.45rem;
    font-weight: 700;
}
.ns-table-wrap { padding: 0.4rem 0.6rem; }

/* ── Columns Section Label ─────────────────────────────────────── */
.audit-columns-header {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #64748b;
    margin-bottom: 0.4rem;
    padding: 0 0.1rem;
}

/* ── Columns List ──────────────────────────────────────────────── */
.audit-columns-list {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
}

.audit-col-item {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
    transition: border-color 0.15s, box-shadow 0.15s;
}
.audit-col-item.is-open {
    border-color: #bfdbfe;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.06);
}

/* ── Column Header Row ─────────────────────────────────────────── */
.col-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.38rem 0.65rem;
    background: #f8fafc;
    cursor: pointer;
    user-select: none;
    gap: 0.5rem;
    transition: background 0.12s;
}
.col-header:hover { background: #f1f5f9; }
.is-open .col-header { background: #eff6ff; }

.col-header-left {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    min-width: 0;
    flex: 1;
    overflow: hidden;
}
.col-toggle-icon {
    font-size: 0.6rem;
    color: #94a3b8;
    flex-shrink: 0;
    transition: transform 0.15s;
}
.col-path {
    font-weight: 600;
    color: #2563eb;
    font-size: 0.78rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.col-flat-name {
    font-size: 0.65rem;
    background: #e0e7ff;
    color: #4338ca;
    border-radius: 3px;
    padding: 0.05rem 0.35rem;
    font-family: 'SFMono-Regular', Consolas, monospace;
    white-space: nowrap;
    flex-shrink: 0;
}

.col-header-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
}

/* Fill bar */
.fill-bar-wrap {
    display: flex;
    align-items: center;
    gap: 0.3rem;
}
.fill-bar-track {
    width: 52px;
    height: 5px;
    background: #e2e8f0;
    border-radius: 999px;
    overflow: hidden;
}
.fill-bar-fill {
    height: 100%;
    border-radius: 999px;
    transition: width 0.3s ease;
}
.fill-pct {
    font-size: 0.68rem;
    font-weight: 700;
    min-width: 32px;
    text-align: right;
}

/* Cardinality badge */
.card-badge {
    font-size: 0.62rem;
    font-weight: 700;
    border-radius: 3px;
    padding: 0.1rem 0.4rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
}
.card-unique { background: #fef9c3; color: #92400e; }
.card-medium { background: #dbeafe; color: #1d4ed8; }
.card-low    { background: #dcfce7; color: #15803d; }

/* Stat chips */
.stat-chip {
    font-size: 0.65rem;
    color: #64748b;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    padding: 0.1rem 0.35rem;
    white-space: nowrap;
}
.stat-chip i { font-size: 0.6rem; margin-right: 0.1rem; }

/* ── Column Body (expanded) ────────────────────────────────────── */
.col-body {
    padding: 0.5rem 0.65rem 0.6rem;
    background: #fff;
    border-top: 1px solid #e2e8f0;
}
.col-tables-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: flex-start;
}
.col-table-block {
    min-width: 160px;
    flex: 1 1 160px;
}
.col-table-label {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #94a3b8;
    margin-bottom: 0.25rem;
}
.col-no-data {
    font-size: 0.72rem;
    color: #94a3b8;
    font-style: italic;
}

/* ── Shared Audit Table ────────────────────────────────────────── */
.audit-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.72rem;
}
.audit-table thead th {
    padding: 0.22rem 0.45rem;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #94a3b8;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}
.audit-table tbody td {
    padding: 0.22rem 0.45rem;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
    word-break: break-word;
}
.audit-table tbody tr:last-child td { border-bottom: none; }
.audit-table tbody tr:hover td { background: #f8fafc; }

/* ── Fill badges (namespace table) ────────────────────────────── */
.fill-badge {
    font-size: 0.65rem;
    font-weight: 700;
    border-radius: 999px;
    padding: 0.1rem 0.5rem;
}
.fill-high    { background: #dcfce7; color: #15803d; }
.fill-mid     { background: #fef9c3; color: #92400e; }
.fill-low     { background: #fee2e2; color: #b91c1c; }
.fill-unknown { background: #f1f5f9; color: #64748b; }

/* ── Namespace code ────────────────────────────────────────────── */
.ns-code {
    font-size: 0.72rem;
    background: #eef2ff;
    color: #4f46e5;
    border-radius: 3px;
    padding: 0.08rem 0.35rem;
    font-family: 'SFMono-Regular', Consolas, monospace;
}
</style>
<style lang="scss">
@use '../theme/scss/_user-variables.scss' as *;

/* ── Dark Mode ─────────────────────────────────────────────────── */
body.dark-mode {
    .audit-viewer {
        color: $dark-text-primary;
    }

    .audit-summary-bar { 
        background: rgba($dark-body-bg, 0.5); 
        border-color: $dark-border-color; 
    }
    .summary-pill { 
        background: $dark-input-bg; 
        border-color: $dark-border-color; 
    }
    .pill-value { 
        color: $dark-text-primary; 
    }
    
    /* Optional: Customizing the format pill to match dark mode accents */
    .source-format-pill { 
        background: rgba($dark-accent, 0.1); 
        border-color: rgba($dark-accent, 0.3); 
    }
    .source-format-pill .pill-value { 
        color: $dark-accent; 
    }

    .audit-ctrl-btn { 
        background: $dark-card-bg; 
        border-color: $dark-border-color; 
        color: $dark-text-secondary; 
    }
    .audit-ctrl-btn:hover { 
        background: $dark-hover-bg; 
        color: $dark-text-primary; 
    }

    .audit-section-block { 
        border-color: $dark-border-color; 
    }
    .audit-section-header { 
        background: $dark-card-bg; 
        color: $dark-text-primary; 
    }
    .audit-section-header:hover { 
        background: $dark-hover-bg; 
    }
    .section-count { 
        background: $dark-elevated-bg; 
        color: $dark-text-secondary; 
    }

    .audit-columns-header { 
        color: $dark-text-secondary; 
    }

    .audit-col-item { 
        border-color: $dark-border-color; 
    }
    .audit-col-item.is-open { 
        border-color: rgba($dark-accent, 0.5); 
        box-shadow: 0 0 0 2px rgba(44, 123, 229, 0.15); 
    }

    .col-header { 
        background: $dark-card-bg; 
    }
    .col-header:hover { 
        background: $dark-hover-bg; 
    }
    .is-open .col-header { 
        background: #1e3a5fa9; 
    }

    .col-path { 
        color: $dark-accent; 
    }
    .col-flat-name { 
        background: $dark-input-bg; 
        color: $dark-text-secondary; 
        border: 1px solid $dark-border-color; 
    }

    .stat-chip { 
        background: $dark-input-bg; 
        border-color: $dark-border-color; 
        color: $dark-text-secondary; 
    }
    .fill-bar-track { 
        background: $dark-border-color; 
    }

    .col-body { 
        background: rgba($dark-body-bg, 0.5); 
        border-top-color: $dark-border-color; 
    }
    .col-table-label { 
        color: $dark-text-secondary; 
    }

    .audit-table thead th { 
        background: $dark-card-bg; 
        border-color: $dark-border-color; 
        color: $dark-text-secondary; 
    }
    .audit-table tbody td { 
        border-color: $dark-border-color; 
        color: $dark-text-primary; 
    }
    .audit-table tbody tr:hover td { 
        background: $dark-hover-bg; 
    }

    .ns-code { 
        background: $dark-input-bg; 
        color: $dark-text-secondary; 
        border: 1px solid $dark-border-color; 
    }
    .col-no-data { 
        color: $dark-text-muted; 
    }

    /* Distribution Pills */
    .dist-pill { 
        background: $dark-card-bg; 
        border-color: $dark-border-color; 
    }
    .dist-val { 
        color: $dark-text-primary; 
    }
    .dist-cnt { 
        background: $dark-elevated-bg; 
        color: $dark-text-secondary; 
        border-left-color: $dark-border-color; 
    }
    .card-unique {
        background: #92400e;
        color: #ffea00;
    }
    
    .card-medium {
        background: #1d4ed8;
        color: #dbeafe;
    }
    
    .card-low {
        background: #15803d;
        color: #dcfce7;
    }
}
</style>